import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import localStorage from '../../utils/localStorage';
import { API_DEVICE, MASTER } from '../../commons/api';
import { Button, Input, Modal, Select, Tree } from 'antd';
import { DownloadOutlined, UpSquareOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TreeNode } = Tree;

// Utility: rename object keys recursively
const renameProperty = (obj, oldName1, newName1, oldName2, newName2) => {
  if (!obj) return obj;
  const newObj = { ...obj };
  if (Array.isArray(newObj.children)) {
    newObj.children = newObj.children.map(child =>
      renameProperty(child, oldName1, newName1, oldName2, newName2)
    );
  }
  if (oldName1 in newObj) {
    newObj[newName1] = newObj[oldName1];
    delete newObj[oldName1];
  }
  if (oldName2 in newObj) {
    newObj[newName2] = newObj[oldName2];
    delete newObj[oldName2];
  }
  return newObj;
};

const SelectComponent = (props) => {
  const [hierarchyData, setHierarchyData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deviceList, setDeviceList] = useState([]);
  const [optionList, setOptionList] = useState([]);

  const fetchDevices = useCallback(async () => {
    try {
      const accessToken = localStorage.get('accessToken');
      const siteId = localStorage.get('currentSite');
      const response = await axios.get(`${API_DEVICE.GET}?SiteId=${siteId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return response.data || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    fetchDevices().then(setDeviceList);
  }, [fetchDevices]);

  const getDevicesList = async (payload) => {
    try {
      const accessToken = localStorage.get('accessToken');
      const siteId = localStorage.get('currentSite');
      const params = payload.GETParams ? `?SiteId=${siteId}${payload.GETParams}` : `?SiteId=${siteId}`;
      const response = await axios({
        method: payload.method,
        url: `${payload.url}${params}`,
        headers: { Authorization: `Bearer ${accessToken}` },
        data: payload.payload || {}
      });
      return response.data || [];
    } catch {
      return [];
    }
  };

  const getOptionList = useCallback(async () => {
    const { column } = props;
    if (column?.type === 'Hierarchy') {
      const raw = await getDevicesList(column.payload);
      const formatted = raw.map(item =>
        renameProperty(item, 'Name', 'title', 'key', 'key')
      );
      setHierarchyData(formatted);
    } else if (column?.table) {
      if (Array.isArray(column.table)) {
        setOptionList(column.table);
        return;
      }
      const accessToken = localStorage.get('accessToken');
      const siteId = localStorage.get('currentSite');
      let config = {
        headers: { Authorization: `Bearer ${accessToken}` }
      };

      if (column.table.url) {
        config = {
          ...config,
          method: column.table.method || 'GET',
          url: column.table.method === 'POST'
            ? `${column.table.url}?SiteId=${siteId}`
            : `${column.table.url}&SiteId=${siteId}`,
          data: column.table.payload || {}
        };
      } else {
        config = {
          method: 'POST',
          url: `${MASTER.DropDown}?SiteId=${siteId}`,
          data: { tableName: column.table }
        };
      }

      try {
        const response = await axios(config);
        const data = Array.isArray(response.data) ? response.data : [];
        setOptionList(data);

        if (data.length > 0 && column.option?.value && props.initalRender) {
          props.handleFieldChange(
            data[0][column.option.value],
            { optionList: data, initalRender: false },
            column
          );
        }
      } catch {
        // swallow error
      }
    }
  }, [props, getDevicesList]);

  useEffect(() => {
    getOptionList();
  }, [getOptionList]);

  const handleSelectDevice = (deviceCode) => {
    props.handleFieldChange(deviceCode, { optionList, initalRender: false }, props.column);
  };

  const renderTreeNodes = (data) =>
    data.map(item =>
      item.children?.length ? (
        <TreeNode title={<div>{item.title}</div>} key={item.key}>
          {renderTreeNodes(item.children)}
        </TreeNode>
      ) : (
        <TreeNode
          title={<div onClick={() => handleSelectDevice(item.key)}>{item.title}</div>}
          key={item.key}
        />
      )
    );

  const { column, record } = props;
  const filtered = deviceList.filter(d => d.DeviceCode === column?.defaultValue);

  return (
    <div>
      {column?.type !== 'Hierarchy' ? (
        <div>
          <strong style={{ color: 'white' }}>{column.Name}</strong>
          <Select
            value={column.defaultValue || undefined}
            style={{ width: 320, margin: '0 5px' }}
            onChange={(value) =>
              props.handleFieldChange(value, { optionList, initalRender: false }, column)
            }
            onKeyPress={(e) => props.handleKeyPress(e, record.key)}
            placeholder={column.placeHolder || column.displayName}
          >
            {optionList.map((opt, idx) => {
              if (
                column.option?.value &&
                column.option.display &&
                opt[column.option.value] !== undefined
              ) {
                return (
                  <Option key={idx} value={opt[column.option.value]}>
                    {opt[column.option.display]}
                  </Option>
                );
              }
              return (
                <Option key={idx} value={opt}>
                  {opt}
                </Option>
              );
            })}
          </Select>
        </div>
      ) : (
        <span style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
          <Input
            value={filtered[0]?.Name || ''}
            style={{ width: 300, height: 32 }}
          />
          <Button type="primary" onClick={() => setIsModalVisible(true)} style={{ marginLeft: 8 }}>
            <UpSquareOutlined />
          </Button>
          <Modal
            title="Select Hierarchy"
            visible={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
            bodyStyle={{ height: 900, overflowY: 'scroll' }}
          >
            <Tree showLine defaultExpandAll switcherIcon={<DownloadOutlined />}>
              {renderTreeNodes(hierarchyData)}
            </Tree>
          </Modal>
        </span>
      )}
    </div>
  );
};

export default SelectComponent;
